import { useEffect, useRef, useMemo } from 'react';
import { getToken } from '@onemedical/auth';
import { getCodaConfig } from '../config/codaConfig';
import { CodaWidgetPosition, OnelifeCodaElement } from '../types';
import { useProfile } from '../../app/hooks/useProfile';

export interface CodaWidgetOptions {
  position?: CodaWidgetPosition;
  expanded?: boolean;
  [key: string]: string | boolean | undefined | null;
}

export interface CodaWidgetHookResult {
  containerRef: React.RefObject<HTMLDivElement>;
  widgetElementRef: React.RefObject<OnelifeCodaElement | null>;
  isConfigured: boolean;
}

interface UserContext {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
}

const createWidgetElement = (
  config: NonNullable<ReturnType<typeof getCodaConfig>>,
  options: CodaWidgetOptions,
): OnelifeCodaElement => {
  const widgetElement = document.createElement('onelife-coda') as OnelifeCodaElement;

  widgetElement.setAttribute('theme', config.theme);
  widgetElement.setAttribute('position', options.position || 'bottom-right');

  if (options.expanded !== undefined) {
    widgetElement.setAttribute('expanded', options.expanded.toString());
  }

  if (config.agentUrl) {
    widgetElement.setAttribute('agent-url', config.agentUrl);
  }

  return widgetElement;
};

const configureWidgetAuth = (widgetElement: OnelifeCodaElement, authToken: string | null): void => {
  if (!authToken) return;

  try {
    widgetElement.setAuthHeaders({
      Authorization: `Bearer ${authToken}`,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[coda] Error setting auth headers:', error);
  }
};

const configureWidgetContext = (
  widgetElement: OnelifeCodaElement,
  userContext: UserContext,
): void => {
  if (!userContext) return;

  try {
    widgetElement.setUserContext(userContext);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[coda] Error setting user context:', error);
  }
};

export function useCodaWidget(options: CodaWidgetOptions = {}): CodaWidgetHookResult {
  const config = getCodaConfig();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<OnelifeCodaElement | null>(null);
  const authToken = getToken();
  const { id, displayName, email, primaryRole } = useProfile();

  const userContext = useMemo<UserContext>(
    () => ({
      id,
      name: displayName,
      email,
      role: primaryRole,
    }),
    [id, displayName, email, primaryRole],
  );

  useEffect(() => {
    if (!config) return undefined;

    const scriptUrl = `${config.host}${config.src}`;
    let script: HTMLScriptElement | null = null;

    const existingScript = document.querySelector(
      `script[src="${scriptUrl}"]`,
    ) as HTMLScriptElement | null;
    if (!existingScript) {
      script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;

      document.body.appendChild(script);
    }

    return () => {
      if (script && document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [config]);

  useEffect(() => {
    if (!config || !containerRef.current) return undefined;

    const currentContainer = containerRef.current;

    const initializeWidget = async () => {
      try {
        await customElements.whenDefined('onelife-coda');

        if (widgetRef.current || !containerRef.current || !config) return;

        // Create the widget element
        const widgetElement = createWidgetElement(config, options);

        // Set the widget reference first
        widgetRef.current = widgetElement;

        // Configure auth and context
        configureWidgetAuth(widgetElement, authToken);
        configureWidgetContext(widgetElement, userContext);

        // Finally append to the container
        if (containerRef.current) {
          containerRef.current.appendChild(widgetElement);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[coda] Error initializing widget:', error);
      }
    };

    initializeWidget();

    return () => {
      if (widgetRef.current && currentContainer) {
        try {
          currentContainer.removeChild(widgetRef.current);
          widgetRef.current = null;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[coda] Error removing widget element:', error);
        }
      }
    };
  }, [config, options, authToken, userContext]);

  return { containerRef, widgetElementRef: widgetRef, isConfigured: !!config };
}
